import type { User } from "@/api/hooks/useMe";
import { ArrowBack, Notifications, Wifi, WifiOff } from "@mui/icons-material";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { AppBar, Badge, Box, IconButton, Toolbar } from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useAudio } from "../contexts/AudioContexts";
import { useJobsContext } from "../contexts/JobsContexts";
import ShiftInfo from "./ShiftInfo";

type AppHeaderProps = {
  onBack?: () => void;
  me: User | undefined;
};

export function AppHeader({ onBack, me }: AppHeaderProps) {
  const { pendingJobs } = useJobsContext();
  const { isEnabled } = useAudio();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: "#fcfcfcff",
        display: "block",
        pt: 0,
        pb: 0,
      }}
    >
      <Toolbar variant="dense" sx={{ minHeight: 60, px: 0 }}>
        {onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              mr: 1,
              ml: 0,
              p: 0.6,
            }}
          >
            <ArrowBack sx={{ fontSize: "1.6rem", p: 0, color: "#2587BC" }} />
          </IconButton>
        )}

        <img
          src="../raptor-logo.svg"
          style={{ width: "55px", height: "55px" }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          {isEnabled ? (
            <VolumeDownIcon
              sx={{
                color: (theme) => theme.palette.primary.main,
                fontSize: "1.3rem",
              }}
            />
          ) : (
            <VolumeOffIcon
              sx={{
                color: (theme) => theme.palette.error.light,
                fontSize: "1.3rem",
              }}
            />
          )}

          <Link to="/jobs" asChild>
            <IconButton>
              <Badge
                badgeContent={pendingJobs}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.70rem",
                    fontWeight: 600,
                    background: (theme) => theme.palette.primary.main,
                  },
                }}
              >
                <Notifications sx={{ color: "#5a5a5a", fontSize: "1.3rem" }} />
              </Badge>
            </IconButton>
          </Link>

          {isOnline ? (
            <Wifi sx={{ color: "#5a5a5a", fontSize: "1.3rem" }} />
          ) : (
            <WifiOff sx={{ color: "#dc2626", fontSize: "1.3rem" }} />
          )}

          <ShiftInfo me={me} />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
